import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Menu, Search, ChevronDown, User, Settings, LogOut, CheckCheck, Download } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import {
  subscribeToAdminAlerts,
  markAlertAsRead,
  markAllAlertsAsRead,
  AdminAlert,
  getAdminAlertRoute,
} from '@/services/adminAlerts';
import { requestPushPermission, onForegroundMessage } from '@/services/webPush';
import { WithId } from '@/types/firestore';

interface HeaderProps {
  title: string;
  onMenuClick: () => void;
}

const Header = ({ title, onMenuClick }: HeaderProps) => {
  const { user, signOut } = useAuthStore();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [alerts, setAlerts] = useState<WithId<AdminAlert>[]>([]);
  const [toast, setToast] = useState<{ title: string; body: string } | null>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  // Subscribe to real-time admin alerts
  useEffect(() => {
    const unsubscribe = subscribeToAdminAlerts((newAlerts) => {
      setAlerts(newAlerts);
    }, 10);
    return () => unsubscribe();
  }, []);

  // PWA install prompt
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setInstallPrompt(null);
    }
  };

  // Register web push notifications
  useEffect(() => {
    if (!user?.uid) return;
    requestPushPermission(user.uid).catch(() => {/* silent — user may have denied */});

    let cleanupForeground: (() => void) | null = null;
    onForegroundMessage((payload) => {
      // Show in-app toast for foreground notifications
      setToast({ title: payload.title, body: payload.body });
      setTimeout(() => setToast(null), 5000);
    }).then((unsub) => { cleanupForeground = unsub; });

    return () => { cleanupForeground?.(); };
  }, [user?.uid]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadCount = alerts.length;

  const formatTimeAgo = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Optimistic update — UI first, Firestore in background
  const handleMarkAllRead = () => {
    const previous = [...alerts];
    setAlerts([]);
    setShowNotifications(false);
    markAllAlertsAsRead().catch((err) => {
      console.error('Error marking all as read:', err);
      setAlerts(previous); // Rollback on failure
    });
  };

  // Optimistic update — navigate immediately, mark read in background
  const handleAlertClick = (alert: WithId<AdminAlert>) => {
    // Immediately update UI
    setAlerts((prev) => prev.filter((a) => a.id !== alert.id));
    setShowNotifications(false);
    navigate(getAdminAlertRoute(alert));

    // Fire-and-forget Firestore write
    markAlertAsRead(alert.id).catch((err) => {
      console.error('Error marking alert as read:', err);
    });
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="flex items-center justify-between px-4 lg:px-6 h-16">
          {/* Left side */}
          <div className="flex items-center gap-4">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl lg:text-2xl font-semibold text-gray-900">{title}</h1>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2 lg:gap-4">
            {/* Install App is in the user dropdown menu (before Sign Out) */}
            {/* Search (Desktop) */}
            <div className="hidden md:flex items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="w-64 h-10 pl-10 pr-4 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  setShowUserMenu(false);
                }}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg relative"
              >
                <Bell className="w-6 h-6" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>

              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
                  >
                    {/* Header row */}
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">
                        Notifications
                        {unreadCount > 0 && (
                          <span className="ml-2 text-xs bg-red-100 text-red-600 font-semibold px-1.5 py-0.5 rounded-full">
                            {unreadCount}
                          </span>
                        )}
                      </h3>
                      {alerts.length > 0 && (
                        <button
                          onClick={handleMarkAllRead}
                          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium"
                        >
                          <CheckCheck className="w-3.5 h-3.5" />
                          Mark all read
                        </button>
                      )}
                    </div>

                    {/* List */}
                    <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
                      {alerts.length === 0 ? (
                        <div className="px-4 py-8 text-center">
                          <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">All caught up!</p>
                        </div>
                      ) : (
                        alerts.map((alert) => (
                          <button
                            key={alert.id}
                            type="button"
                            onClick={() => handleAlertClick(alert)}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 active:bg-gray-100 transition-colors bg-purple-50/40"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-2 h-2 bg-primary rounded-full mt-1.5 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{alert.title}</p>
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{alert.message}</p>
                                <p className="text-xs text-gray-400 mt-1">{formatTimeAgo(alert.createdAt)}</p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>

                    {/* Footer */}
                    <Link
                      to="/admin/notifications-center"
                      onClick={() => setShowNotifications(false)}
                      className="block px-4 py-2.5 text-center text-sm font-medium text-primary hover:bg-gray-50 border-t border-gray-100 transition-colors"
                    >
                      View all notifications →
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* User Menu */}
            <div className="relative" ref={userRef}>
              <button
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                  setShowNotifications(false);
                }}
                className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded-lg transition-colors"
              >
                {user?.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'User'}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary font-semibold text-sm">
                      {user?.displayName?.[0] || 'A'}
                    </span>
                  </div>
                )}
                <div className="hidden lg:block text-left">
                  <p className="text-sm font-medium text-gray-900 max-w-[120px] truncate">
                    {user?.displayName || 'Admin'}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-500 hidden lg:block" />
              </button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="font-medium text-gray-900">{user?.displayName}</p>
                      <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/admin/config"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50"
                      >
                        <User className="w-4 h-4" />
                        <span className="text-sm">Profile</span>
                      </Link>
                      <Link
                        to="/admin/config"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50"
                      >
                        <Settings className="w-4 h-4" />
                        <span className="text-sm">Settings</span>
                      </Link>
                    </div>
                    <div className="py-1 border-t border-gray-100">
                      {/* PWA Install — in user menu */}
                      {!isInstalled && (
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            if (installPrompt) {
                              handleInstallClick();
                            } else {
                              alert('To install as an app:\n\n📱 Mobile: Chrome menu (⋮) → "Add to Home screen"\n💻 Desktop: Click the ⊕ icon in Chrome\'s address bar\n\nMake sure you\'re on nepro.in/admin');
                            }
                          }}
                          className="flex items-center gap-3 px-4 py-2 w-full text-primary hover:bg-primary/5"
                        >
                          <Download className="w-4 h-4" />
                          <span className="text-sm">Install App</span>
                        </button>
                      )}
                      <button
                        onClick={signOut}
                        className="flex items-center gap-3 px-4 py-2 w-full text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4" />
                        <span className="text-sm">Sign Out</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* In-app toast for foreground FCM messages */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 60 }}
            className="fixed top-20 right-4 z-[100] max-w-sm bg-white rounded-xl shadow-lg border border-gray-200 p-4 cursor-pointer"
            onClick={() => {
              setToast(null);
              navigate('/admin/notifications-center');
            }}
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Bell className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900">{toast.title}</p>
                <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{toast.body}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
